import { LocalizedText } from "@/i18n/LocalizedText";
import { useEffect } from "react";
import { Label } from "@/components/ui/label";
import { Controller, useForm } from "react-hook-form";
import type { FieldError } from "react-hook-form";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { type AuthContextType } from "../components/AuthContext";
import { Check, Upload } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Select,
  SelectContent,
  SelectValue,
  SelectTrigger,
  SelectItem,
  SelectGroup,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { useLocale } from "@/i18n/LocaleProvider";
import type { AppLocale } from "@/i18n/locale";

type FormValues = {
  email: string;
  fullname: string;
  theme: string;
  language: AppLocale;
  timeZone: string;
};

const themes = [
  {
    name: "Light",
    value: "light",
    disabled: false,
  },
  {
    name: "Dark",
    value: "dark",
    disabled: true,
  },
];

const ProfilePage = ({ user }: { user: AuthContextType["user"] }) => {
  const { locale, setLocale, t } = useLocale();
  const {
    register,
    formState: { errors },
    setValue,
  } = useForm();
  useEffect(() => {
    setValue("email", user?.email || "");
    setValue("fullname", user?.name || "");
  }, [user]);
  const {
    handleSubmit,
    control,
    setValue: setFormValue,
  } = useForm<FormValues>({
    defaultValues: {
      email: user?.email || "",
      fullname: user?.name || "",
      theme: "light",
      language: locale,
      timeZone: "Eastern Standard Time (EST)",
    },
  });

  useEffect(() => {
    setFormValue("language", locale);
  }, [locale, setFormValue]);

  const onSubmit = (data: FormValues) => {
    setLocale(data.language);
    toast.success(t("profile.saved"), {
      icon: (
        <div className="w-6 h-6 bg-muted rounded-lg flex items-center justify-center">
          <Check className="w-4 h-4 text-black" />
        </div>
      ),
      position: "bottom-right",
    });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="p-5">
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-1">
          <h1 className="text-base text-[#0A0A0A] font-semibold">
            {t("profile.title")}
          </h1>
          <div className="text-muted-foreground text-sm">
            {t("profile.subtitle")}
          </div>
        </div>
        <div className="flex gap-4 flex-wrap">
          <div>
            <Label
              className="mb-2 text-sm font-medium text-foreground"
              htmlFor="email"
            >
              {t("profile.email")}
            </Label>
            <Input
              disabled={true}
              id="email"
              type="email"
              autoComplete="off"
              {...register("email")}
              className="w-full font-normal text-muted-foreground text-sm px-4 py-2 border rounded-md focus:outline-none focus:ring-1 focus:ring-slate-300"
            />
            {errors.email && (
              <p className="text-red-500 text-sm mt-2">
                {(errors.email as FieldError)?.message}
              </p>
            )}
          </div>
          <div>
            <Label
              className="mb-2 text-sm font-medium text-foreground"
              htmlFor="fullname"
            >
              {t("profile.fullName")}
            </Label>
            <Input
              disabled={true}
              id="fullname"
              type="text"
              autoComplete="off"
              {...register("fullname")}
              className="w-full font-normal text-muted-foreground text-sm px-4 py-2 border rounded-md focus:outline-none focus:ring-1 focus:ring-slate-300"
            />
            {errors.fullname && (
              <p className="text-red-500 text-sm mt-2">
                {(errors.fullname as FieldError)?.message}
              </p>
            )}
          </div>
          <div>
            <Label
              className="mb-2 text-sm font-medium text-foreground"
              htmlFor="profilePicture"
            >
              {t("profile.picture")}
            </Label>
            <div className="flex gap-2">
              <Avatar className="h-8 w-8 rounded">
                {user?.picture && (
                  <AvatarImage
                    src={user?.picture}
                    alt={t("legacy.user.avatar.1ptphvp")}
                  />
                )}

                <AvatarFallback>
                  {" "}
                  {user?.name
                    ?.split(" ")
                    .map((n) => n[0])
                    .join("")
                    .toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <Button disabled={true} variant="outline">
                <Upload /> {t("profile.upload")}
              </Button>
            </div>
          </div>
        </div>
        <Separator className="mb-5" />
      </div>

      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-1">
          <h1 className="text-base text-[#0A0A0A] font-semibold">
            {t("profile.languageLocale.title")}{" "}
          </h1>
          <div className="text-muted-foreground text-sm">
            {t("profile.languageLocale.subtitle")}
          </div>
        </div>
        <div className="flex gap-4 flex-wrap">
          <div>
            <Controller
              name="language"
              control={control}
              render={({ field }) => {
                return (
                  <>
                    <Label
                      className="mb-2 text-sm font-medium text-muted-foreground"
                      htmlFor="language"
                    >
                      {t("profile.language")}
                    </Label>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger className="w-fit min-w-[266px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectGroup>
                          <SelectItem value="en">
                            {t("locale.english")}
                          </SelectItem>
                          <SelectItem value="uk">
                            {t("locale.ukrainian")}
                          </SelectItem>
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                  </>
                );
              }}
            />
          </div>
          <div>
            <Controller
              name="timeZone"
              control={control}
              render={({ field }) => {
                return (
                  <>
                    <Label
                      className="mb-2 text-sm font-medium text-muted-foreground"
                      htmlFor="timeZone"
                    >
                      {t("profile.timeZone")}
                    </Label>
                    <Select
                      value={field.value}
                      onValueChange={field.onChange}
                      disabled
                    >
                      <SelectTrigger className="w-fit min-w-[266px] text-slate-400 ">
                        <SelectValue placeholder={field.value}>
                          {field.value}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectGroup>
                          <SelectItem value="german">
                            <LocalizedText id="legacy.german.16ezhbr" />
                          </SelectItem>
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                  </>
                );
              }}
            />
          </div>
        </div>
        <Separator className="mb-5" />
      </div>

      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-1">
          <h1 className="text-base text-[#0A0A0A] font-semibold">
            {t("profile.appearance.title")}
          </h1>
          <div className="text-muted-foreground text-sm">
            {t("profile.appearance.subtitle")}
          </div>
        </div>
        <div>
          <div className="space-y-4">
            <p className="text-sm font-medium">{t("profile.theme")}</p>
            <div className="flex gap-4 flex-wrap">
              <Controller
                name="theme"
                control={control}
                render={({ field }) => (
                  <div className="flex gap-4 flex-wrap">
                    {themes.map((theme) => {
                      const isDisabled = theme.disabled;
                      const isSelected = field.value === theme.value;
                      return (
                        <div
                          key={theme.value}
                          className={`relative p-2 w-[200px] cursor-pointer transition ${isDisabled && "opacity-50"}`}
                          onClick={() => {
                            if (!isDisabled) field.onChange(theme.value);
                          }}
                        >
                          <div
                            className={`flex flex-col gap-2 space-y-2 rounded-md p-2 ${theme.value === "light" ? "bg-[#E5E5E5]" : "bg-[#171717]"} ${isSelected ? "border-[#FF5E00] border-[1.5px] ring-1 ring-[#FF5E00]" : "border-muted"}`}
                          >
                            <div
                              className={`p-3 ${theme.value === "light" ? "bg-white" : "bg-[#262626]"} gap-2 flex flex-col rounded-md`}
                            >
                              <div
                                className={`h-[8px] w-1/3 rounded ${theme.value === "dark" ? "bg-[#737373]" : "bg-[#E5E5E5]"} `}
                              />
                              <div
                                className={`h-[8px] w-2/3 rounded ${theme.value === "dark" ? "bg-[#737373]" : "bg-[#E5E5E5]"} `}
                              />
                            </div>

                            <div
                              className={`flex p-3 ${theme.value === "light" ? "bg-white" : "bg-[#262626]"} items-center space-x-4 rounded-md`}
                            >
                              <div
                                className={`h-[14px] w-[16px] rounded-full ${theme.value === "light" ? "bg-[#E5E5E5]" : "bg-[#737373]"} rounded`}
                              />
                              <div className="space-y-2 w-full">
                                <div
                                  className={`h-[8px] w-full ${theme.value === "light" ? "bg-[#E5E5E5]" : "bg-[#737373]"} rounded`}
                                />
                              </div>
                            </div>

                            <div
                              className={`flex p-3 ${theme.value === "light" ? "bg-white" : "bg-[#262626]"} items-center space-x-4 rounded-md`}
                            >
                              <div
                                className={`h-[14px] w-[16px] rounded-full ${theme.value === "light" ? "bg-[#E5E5E5]" : "bg-[#737373]"} rounded`}
                              />
                              <div className="space-y-2 w-full">
                                <div
                                  className={`h-[8px] w-full ${theme.value === "light" ? "bg-[#E5E5E5]" : "bg-[#737373]"} rounded`}
                                />
                              </div>
                            </div>
                          </div>

                          <p className="text-center mt-2 text-sm font-medium text-muted-foreground">
                            {t(`profile.theme.${theme.value}`)}{" "}
                            {isDisabled && (
                              <Badge
                                variant="secondary"
                                className="ml-1 text-xs text-muted-foreground"
                              >
                                {t("profile.badge.soon")}
                              </Badge>
                            )}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                )}
              />
            </div>
          </div>
        </div>
        <Separator />
        <div className="flex justify-center w-full">
          <Button
            variant="outline"
            className="w-fit hover:bg-[#ff8138] hover:text-white text-white cursor-pointer bg-[#FF5E00]"
          >
            {t("profile.saveChanges")}
          </Button>
        </div>
      </div>
    </form>
  );
};

export default ProfilePage;
